import { inferenceHttp } from '@/lib/api/http-client';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';
import { AudienceService } from '@inference.engine/api-client/web';
import { useMutation, useQueryClient } from '@tanstack/react-query';

/**
 * ProfileMediaTab owns the error UI for persona media, so the global interceptor
 * must stay quiet: video generation fires two requests and would stack one toast
 * per failure, and image regeneration would pair its toast with the tab's own.
 */
// eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
const SUPPRESS_GLOBAL_TOAST = { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' };

interface UsePersonaMediaGenerationParams {
  personaId: string;
}

interface RegenerateImageParams {
  /** Extra appearance instructions. Omitted, the generator builds the prompt from the persona. */
  prompt?: string;
  model: string;
}
interface GenerateVideosParams {
  model: string;
  prompt?: string;
  /** Source photo for the videos. Defaults to the persona's saved photo when omitted. */
  photoAssetId?: string;
}

export type RetryableVideoType = 'mouseOverVideo' | 'video';

interface RetryVideoParams {
  type: RetryableVideoType;
  model: string;
  prompt?: string;
  /** Source photo for the video. Defaults to the persona's saved photo when omitted. */
  photoAssetId?: string;
}

export function usePersonaMediaGeneration({ personaId }: UsePersonaMediaGenerationParams) {
  const queryClient = useQueryClient();
  const workspaceId = useCurrentWorkspaceId();

  const imageGeneration = useMutation({
    mutationKey: ['persona-media', personaId, 'image'],
    mutationFn: async (params: RegenerateImageParams) => {
      const response = await AudienceService.generateAudienceAsset({
        client: inferenceHttp,
        throwOnError: true,
        path: { id: personaId },
        headers: SUPPRESS_GLOBAL_TOAST,
        body: {
          type: 'image',
          model: params.model,
          prompt: params.prompt,
        },
      });

      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['personas', workspaceId] });
    },
  });

  const videoGeneration = useMutation({
    mutationKey: ['persona-media', personaId, 'video'],
    mutationFn: async (params: GenerateVideosParams) => {
      const [mouseOverResult, videoResult] = await Promise.allSettled([
        AudienceService.generateAudienceAsset({
          client: inferenceHttp,
          throwOnError: true,
          path: { id: personaId },
          headers: SUPPRESS_GLOBAL_TOAST,
          body: {
            type: 'mouseOverVideo',
            model: params.model,
            photoAssetId: params.photoAssetId,
          },
        }),
        AudienceService.generateAudienceAsset({
          client: inferenceHttp,
          throwOnError: true,
          path: { id: personaId },
          headers: SUPPRESS_GLOBAL_TOAST,
          body: {
            type: 'video',
            model: params.model,
            prompt: params.prompt,
            photoAssetId: params.photoAssetId,
          },
        }),
      ]);

      return {
        mouseOverAssetId:
          mouseOverResult.status === 'fulfilled' ? mouseOverResult.value.data.assetId : undefined,
        videoAssetId:
          videoResult.status === 'fulfilled' ? videoResult.value.data.assetId : undefined,
        mouseOverError:
          mouseOverResult.status === 'rejected'
            ? (mouseOverResult.reason as Error | undefined)
            : undefined,
        videoError:
          videoResult.status === 'rejected' ? (videoResult.reason as Error | undefined) : undefined,
      };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['personas', workspaceId] });
    },
  });

  // Retry a single video type (thumbnail or detail) with the same model/prompt,
  // so a partial failure can be re-run without regenerating the other video.
  const videoRetry = useMutation({
    mutationKey: ['persona-media', personaId, 'video-retry'],
    mutationFn: async (params: RetryVideoParams) => {
      const response = await AudienceService.generateAudienceAsset({
        client: inferenceHttp,
        throwOnError: true,
        path: { id: personaId },
        headers: SUPPRESS_GLOBAL_TOAST,
        body: {
          type: params.type,
          model: params.model,
          // Prompt only applies to the detail video; thumbnail uses default settings.
          prompt: params.type === 'video' ? params.prompt : undefined,
          photoAssetId: params.photoAssetId,
        },
      });

      return { type: params.type, assetId: response.data.assetId };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['personas', workspaceId] });
    },
  });

  return {
    regenerateImage: imageGeneration.mutateAsync,
    isRegeneratingImage: imageGeneration.isPending,
    regenerateImageError: imageGeneration.error,
    generateVideos: videoGeneration.mutateAsync,
    isGeneratingVideos: videoGeneration.isPending,
    generateVideosError: videoGeneration.error,
    retryVideo: videoRetry.mutateAsync,
    isRetryingVideo: videoRetry.isPending,
  };
}
